import React from 'react'

import {
  AlignItems,
  Box,
  Color,
  FontWeight,
  Icon,
  IconVariant,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import { useSelector } from 'react-redux'

import type { MenuRecipeImage, RecipeImage as Image } from '@library/menu-service'

import { getBrowserType } from 'selectors/browser'
import { getFeaturedImage, getRangeImages } from 'utils/image'

import { DetailCarousel } from '../Carousel'
import { DetailImage } from '../DetailImage'

type RecipeImageProps = {
  title: string
  isHeatOnly?: boolean
  images: {
    partial?: Image[]
    full?: MenuRecipeImage[]
  }
}

const ImageContainer = withCitrusTheme(Box, (theme) => ({
  position: 'relative',
  marginLeft: `-${theme.spacing(4)}px`,
  marginTop: `-${theme.spacing(4)}px`,
  width: `calc(100% + 2*${theme.spacing(4)}px)`,
  [`@media (min-width: ${theme.tokens.breakpoints[0]})`]: {
    marginLeft: `-${theme.spacing(6)}px`,
    marginTop: `-${theme.spacing(6)}px`,
    width: `calc(100% + 2*${theme.spacing(6)}px)`,
  },
}))

const HeatOnlyBanner = withCitrusTheme(Box, (theme) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  padding: `${theme.spacing(2)}px ${theme.spacing(4)}px`,
  background: theme.color(Color.Warning_900),
  color: theme.color(Color.White),
}))

export const RecipeImage = ({ images, title, isHeatOnly = false }: RecipeImageProps) => {
  const { partial: partialImages, full: fullImages } = images
  const browserType = useSelector(getBrowserType)

  const featuredImage = getFeaturedImage(partialImages, 'standard', browserType)
  const rangeImages = getRangeImages(fullImages)

  return (
    <ImageContainer>
      {rangeImages.length > 0 ? (
        <DetailCarousel images={rangeImages} />
      ) : (
        <DetailImage media={featuredImage} title={title} />
      )}
      {isHeatOnly && (
        <HeatOnlyBanner data-testid="heat-only-banner">
          <Text size={1} fontWeight={FontWeight.Bold}>
            HEAT &amp; EAT
          </Text>
          <Box display="flex" alignItems={AlignItems.Center} gap={1}>
            <Icon name="equipment" variant={IconVariant.Inherit} size={4} />
            <Text size={1}>Cooked by us</Text>
          </Box>
          <Box display="flex" alignItems={AlignItems.Center} gap={1}>
            <Icon name="dietary_preference" variant={IconVariant.Inherit} size={4} />
            <Text size={1}>No nasties</Text>
          </Box>
        </HeatOnlyBanner>
      )}
    </ImageContainer>
  )
}
